/* eslint-disable camelcase */
import Modal from 'components/base/Modal';
import AppLoading from 'components/modules/AppLoading';
import FlashNotification from 'components/modules/FlashNotification';
import 'cropperjs/dist/cropper.css';
import React, { useEffect } from 'react';
import 'react-html5-camera-photo/build/css/index.css';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { clearModalContent } from 'redux/modules/modalEvent';
// import Navbar from 'components/base/Navbar';
import ROUTES from 'routes';
import AdminMfaGate from '~/components/modules/AdminMfaGate';
import { setPostLoginRedirect } from '~/utils/authRedirect';
import useAdminAccess from '~/hooks/useAdminAccess';
import useAdminSessionWatcher from '~/hooks/useAdminSessionWatcher';
import useFirebase from '~/hooks/useFirebase';
import useGetWelfareRoles from '~/hooks/useGetWelfareRoles';
import useBranding from './hooks/useBranding';
import useCoBrowse from './hooks/useCoBrowse';
import ScrollToTop from './hooks/useScrollToTop';
import useShowMaintenance from './hooks/useShowMaintenance';
import useWebHistory from './hooks/useWebHistory';
import './utils/debugStorage'; // Make debug functions available globally

function App() {
    useFirebase();
    const { isLoading: IS_LOADING_MAINTENANCE } = useShowMaintenance();

    // useFirebaseNotification();
    useBranding();
    useGetWelfareRoles();

    // Resolves eligibility and admin session state from the server on every
    // authenticated load; both no-op until there is a profile id.
    useAdminAccess();

    // Mounted once, here rather than inside the gate. The gate unmounts on the
    // unauthenticated member-pathway routes, and the warning ladder has to keep
    // running for a session that is still alive underneath one of them.
    useAdminSessionWatcher();

    const dispatch = useAppDispatch();
    const history = useWebHistory();
    const { modalContent, onToggle, ...modalEvent } = useAppSelector(({ modalEvent }) => modalEvent);

    const { isAuthed, isCreatingPassword, isCreatingProfile, isMaintenance } = useAppSelector(({ authentication }) => authentication);
    const { id } = useAppSelector(({ profile }) => profile);

    const location = useLocation();

    useCoBrowse();

    const handleCloseModal = () => {
        dispatch(clearModalContent());
        onToggle && onToggle();

        // To show modal, do this something like this:
        // dispatch(setModalContent({
        //     modalContent: <LoginContainer />,
        //     title: 'Login'
        // }));
    };

    useEffect(() => {
        if (isMaintenance && !location.pathname?.toLowerCase()?.includes('maintenance')) history.push('/maintenance');
    }, [isMaintenance, location.pathname]);

    useEffect(() => {
        if (isCreatingPassword) {
            history.replace('/create-password');
        }
    }, [isCreatingPassword]);

    useEffect(() => {
        if (isCreatingProfile) {
            history.replace('/update-profile');
        }
    }, [isCreatingProfile]);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const groupId = searchParams.get('group_id');

        if (groupId) {
            if (isAuthed && id) {
                history.replace(`/groups?group_id=${groupId}`);
            } else {
                history.replace(`/login${location.search}`);
            }
        }
    }, [isAuthed, id]);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const eventId = searchParams.get('event_id');

        if (eventId) {
            if (isAuthed && id) {
                history.replace(`/events/${eventId}`);
            } else {
                history.replace(`/login${location.search}`);
            }
        }
    }, [isAuthed, id]);

    useEffect(() => {
        if (window.location.pathname === '/') {
            if (isAuthed && id) {
                history.replace('/categories');
            } else {
                history.replace('/login');
            }
        }
    }, [isAuthed, id]);

    return (
        <AppLoading isLoading={IS_LOADING_MAINTENANCE}>
            <ScrollToTop />
            {/* <Header /> */}
            <div id='outer-container'>
                {/* {isAuthed && <Navbar />} */}
                <Switch>
                    {Object.values(ROUTES).map((route, i) => (
                        route.authedRoute
                            ? <PrivateRoute key={i} {...route} isAuthed={isAuthed || false} />
                            : <RouteWithSubRoutes key={i} {...route} />
                    ))}
                </Switch>
            </div>
            <FlashNotification />
            <Modal
                {...modalEvent}
                isShow={!!modalContent &&
                    (typeof modalContent === 'string' ||
                        React.isValidElement(modalContent))}
                onToggle={handleCloseModal}>
                {modalContent}
            </Modal>
            <ToastContainer
                position='top-right'
                autoClose={5000}
                hideProgressBar={true}
                newestOnTop={false}
                closeOnClick={true}
                rtl={false}
                pauseOnFocusLoss={true}
                draggable={false}
                pauseOnHover={true} />
        </AppLoading>
    );
}

// A special wrapper for <Route> that knows how to
// handle "sub"-routes by passing them in a `routes`
// prop to the component it renders.
function RouteWithSubRoutes({ component: Component, ...rest }: { component: any }) {
    return (
        <Route
            {...rest}
            render={props => (
                // pass the sub-routes down to keep nesting
                <Component {...props} />
            )} />
    );
}

function PrivateRoute({ component: Component, isAuthed, ...rest }: { component: any, isAuthed: boolean }) {
    const location = useLocation();

    return (
        <Route {...rest} render={(routeProps) => {
            // Every authenticated route is a welfare staff route, so the gate
            // belongs here rather than on each screen. Nothing added later can
            // miss it.
            if (isAuthed) {
                return (
                    <AdminMfaGate>
                        <Component {...routeProps} />
                    </AdminMfaGate>
                );
            }

            // Remember where the user was headed so we can return them there
            // after they log in (see consumePostLoginRedirect on login success).
            setPostLoginRedirect(`${routeProps.location.pathname}${routeProps.location.search}`);

            return (
                <Redirect to={{
                    pathname: '/login',
                    search: location.search,
                    state: { from: routeProps.location }
                }} />
            );
        }} />
    );
}

export default App;
